import { Component, input, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-textarea',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <textarea
        [id]="id() || name()"
        [name]="name()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [readonly]="readonly()"
        [rows]="rows()"
        [attr.maxlength]="maxLength()"
        [class]="textareaClasses"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onBlur()"
        (focus)="onFocus()"></textarea>
      
      @if (showCharCount() && maxLength()) {
        <div class="text-xs text-gray-500 dark:text-gray-400 text-right">
          {{ value().length }} / {{ maxLength() }}
        </div>
      }
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class TextareaComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  rows = input<number>(4);
  maxLength = input<number | undefined>(undefined);
  showCharCount = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  control = input<AbstractControl | null | undefined>(undefined);
  size = input<ComponentSize>('md');

  value = signal<string>('');
  touched = signal(false);

  private onChange = (value: string) => {};
  private onTouched = () => {};

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.markAsTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  onFocus(): void {
    // Focus handling if needed
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }

  get hasError(): boolean {
    const control = this.control();
    const errorMsg = this.errorMessage();
    if (control) {
      return (control.invalid && (control.touched || this.touched())) || !!errorMsg;
    }
    return this.touched() && !!errorMsg;
  }

  get displayError(): string {
    const errorMsg = this.errorMessage();
    if (errorMsg) return errorMsg;
    const control = this.control();
    if (control && control.errors) {
      if (control.errors['required']) {
        return `${this.label() || 'This field'} is required`;
      }
      if (control.errors['maxlength']) {
        return `Maximum length is ${control.errors['maxlength'].requiredLength}`;
      }
    }
    return '';
  }

  get textareaClasses(): string {
    const classes = [
      'w-full',
      'border',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'resize-y',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:placeholder:text-gray-500'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    classes.push(sizeMap[this.size()]);

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100');
    }

    if (this.readonly()) {
      classes.push('bg-gray-50', 'dark:bg-gray-900');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }
}

